import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import { useAffiliate } from '../../hooks/useAffiliate';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import { checkoutUrlBuilder } from '../../utils/CheckoutUrlBuilder';
import { storeService } from '../../services/api/store/storeApi';
import { API_URL, FRONTEND_URL } from '../../config/api';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import './AffiliateProductDetailsPage.scss';

const AffiliateProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Estados
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [affiliateDetails, setAffiliateDetails] = useState(null);
  const [loadingAffiliateDetails, setLoadingAffiliateDetails] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkForm, setLinkForm] = useState({
    campaign_name: '',
    cookie_duration_hours: 168
  });
  const [creatingLink, setCreatingLink] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  // Hooks
  const { affiliateStatus } = useAffiliate(id);

  // Função para verificar se usuário é afiliado ou gerente aprovado
  const verifyAffiliation = async () => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/${id}/affiliate-details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 404) {
        // Não é afiliado ou gerente aprovado, redirecionar para dashboard
        console.warn('⚠️ Usuário não é afiliado ou gerente deste produto');
        navigate('/Dashboard');
        return false;
      }

      if (!response.ok) {
        throw new Error('Erro ao verificar afiliação');
      }

      setIsVerified(true);
      return true;
    } catch (error) {
      console.error('❌ Erro ao verificar afiliação:', error);
      navigate('/Dashboard');
      return false;
    }
  };

  // Função para buscar detalhes específicos do afiliado ou gerente
  const fetchAffiliateDetails = async () => {
    try {
      setLoadingAffiliateDetails(true);
      console.log('🔍 Buscando detalhes específicos do afiliado ou gerente...');
      
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/${id}/affiliate-details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const roleType = data.data?.affiliation?.role_type || 'affiliate';
        console.log(`✅ Detalhes carregados (${roleType}):`, data);
        console.log('📦 CHECKOUTS:', data.data?.checkouts);
        console.log('🔗 AFFILIATE LINKS:', data.data?.affiliate_links);
        setAffiliateDetails(data.data);
      } else if (response.status === 404) {
        // Não é afiliado ou gerente, redirecionar para dashboard
        navigate('/Dashboard');
      } else {
        const errorData = await response.text();
        console.warn('⚠️ Erro ao buscar detalhes:', response.status, errorData);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar detalhes:', error);
    } finally {
      setLoadingAffiliateDetails(false);
    }
  };

  // Função para criar novo link de afiliado
  const createAffiliateLink = async () => {
    if (!linkForm.campaign_name.trim()) {
      alert('Por favor, digite um nome para a campanha!');
      return;
    }

    try {
      setCreatingLink(true);
      console.log('🔗 Criando link de afiliado:', linkForm);
      
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/${id}/generate-affiliate-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(linkForm)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Link criado:', data);
        
        // Fechar modal primeiro
        setShowLinkModal(false);
        setLinkForm({
          campaign_name: '',
          cookie_duration_hours: 168
        });
        
        // Aguardar um momento e recarregar dados
        setTimeout(() => {
          console.log('🔄 Recarregando dados dos afiliados...');
          fetchAffiliateDetails();
        }, 500);
        
        alert(`Link "${linkForm.campaign_name}" criado com sucesso!`);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Erro ao criar link');
      }
    } catch (error) {
      console.error('❌ Erro ao criar link:', error);
      alert('Erro ao criar link');
    } finally {
      setCreatingLink(false);
    }
  };

  // Função para buscar produto diretamente do backend
  const fetchProduct = async () => {
    try {
      setLoading(true);
      const data = await storeService.getProductDetails(id);
      
      if (data.success) {
        setProduct(data.product);
        console.log('✅ Produto carregado:', data.product);
      } else {
        console.error('❌ Erro ao carregar produto:', data.message);
        navigate('/Dashboard');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar produto:', error);
      navigate('/Dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Obter primeiro link de afiliado disponível
  const getFirstAffiliateLink = () => {
    if (affiliateDetails?.affiliate_links?.length > 0) {
      return affiliateDetails.affiliate_links[0];
    }
    return null;
  };

  // Criar link padrão automático se não existir nenhum
  const createDefaultAffiliateLink = async () => {
    try {
      console.log('🔗 Criando link de afiliado padrão...');
      
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/${id}/generate-affiliate-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campaign_name: 'Link Padrão',
          cookie_duration_hours: 168 // 7 dias
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Link padrão criado:', data);
        
        // Recarregar dados
        await fetchAffiliateDetails();
        
        return data.data;
      } else {
        console.error('❌ Erro ao criar link padrão');
        return null;
      }
    } catch (error) {
      console.error('❌ Erro ao criar link padrão:', error);
      return null;
    }
  };

  // Obter primeiro checkout disponível
  const getFirstCheckout = () => {
    if (affiliateDetails?.checkouts?.length > 0) {
      return affiliateDetails.checkouts[0];
    }
    return null;
  };

  // Função para acessar checkout com link de afiliado
  const handleAccessCheckout = () => {
    const firstLink = getFirstAffiliateLink();
    const firstCheckout = getFirstCheckout();
    // Usar affiliate_code do afiliado (gerado automaticamente na aprovacao)
    const affiliateCode = affiliateDetails?.affiliation?.affiliate_code;

    if (firstCheckout) {
      // Usar checkoutUrlBuilder para gerar URL correta
      const checkoutUrl = checkoutUrlBuilder.buildUrl(
        product,
        firstCheckout,
        affiliateCode || firstLink?.link_code
      );
      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank');
      }
    } else if (!affiliateCode && !firstLink) {
      // Se nao tem affiliate_code nem link, abrir modal para criar
      setShowLinkModal(true);
    } else {
      // Sem checkout disponivel
      alert('Nenhum checkout disponível para este produto.');
    }
  };

  // Funcao para copiar link de checkout
  const handleCopyCheckoutLink = () => {
    const firstLink = getFirstAffiliateLink();
    const firstCheckout = getFirstCheckout();
    // Usar affiliate_code do afiliado (gerado automaticamente na aprovacao)
    const affiliateCode = affiliateDetails?.affiliation?.affiliate_code;

    if (!firstCheckout) {
      alert('Nenhum checkout disponível para copiar.');
      return;
    }

    // Usar checkoutUrlBuilder para gerar URL correta
    const linkToCopy = checkoutUrlBuilder.buildUrl(
      product,
      firstCheckout,
      affiliateCode || firstLink?.link_code
    );

    if (!linkToCopy) {
      alert('Erro ao gerar link de checkout.');
      return;
    }

    navigator.clipboard.writeText(linkToCopy);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  // Buscar dados quando componente monta
  useEffect(() => {
    const initialize = async () => {
      // Primeiro verificar se é afiliado
      const verified = await verifyAffiliation();
      if (verified) {
        await fetchProduct();
        await fetchAffiliateDetails();
      }
    };
    initialize();
  }, [id]);

  // Criar link padrão automaticamente se não existir nenhum
  useEffect(() => {
    const ensureAffiliateLink = async () => {
      if (affiliateDetails && affiliateDetails.affiliate_links?.length === 0) {
        console.log('⚠️ Nenhum link de afiliado encontrado. Criando link padrão...');
        await createDefaultAffiliateLink();
      }
    };
    
    if (affiliateDetails) {
      ensureAffiliateLink();
    }
  }, [affiliateDetails]);

  if (loading || !isVerified) {
    return (
      <MasterLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="text-muted">Carregando detalhes do produto...</p>
          </div>
        </div>
      </MasterLayout>
    );
  }

  if (!product) {
    return (
      <MasterLayout>
        <div className="text-center py-5">
          <Icon icon="mdi:alert-circle" className="text-warning" style={{ fontSize: '48px' }} />
          <span style={{ fontSize: '24px', fontWeight: '600', display: 'block', marginTop: '16px' }}>Produto não encontrado</span>
          <p className="text-muted">O produto que você está procurando não foi encontrado.</p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(-1)}
          >
            Voltar aos Meus Produtos
          </button>
        </div>
      </MasterLayout>
    );
  }

  const firstLink = getFirstAffiliateLink();
  const isApproved = affiliateStatus.isAffiliate && affiliateStatus.status === 'approved';

  // DEBUG: Ver link de afiliado
  console.log('🔗 FIRST LINK:', firstLink);
  console.log('🔗 AFFILIATE DETAILS:', affiliateDetails);

  return (
    <MasterLayout>
      <div className="affiliate-product-details-page">
        {/* Header com Breadcrumb */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
            <div>
              <span style={{ fontSize: '20px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                <Icon icon="mdi:shield-star" className="me-2 text-success" />
                Área do Afiliado
              </span>
              <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '14px' }}>
                <Icon icon="mdi:chevron-right" style={{ fontSize: '12px' }} />
                <span>Produtos que Promovo</span>
                <Icon icon="mdi:chevron-right" style={{ fontSize: '12px' }} />
                <span className="text-primary fw-medium">{product.name}</span>
              </div>
            </div>
            <div className="d-flex gap-3 flex-wrap justify-content-end">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:arrow-left" />
                Voltar
              </button>
            </div>
          </div>
        </div>

        {/* Layout de 2 Colunas */}
        <div className="row gy-4">
          {/* Coluna Esquerda - Imagem e Info do Produto */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4">
                {/* Imagem do Produto */}
                <div
                  className="rounded overflow-hidden bg-light d-flex align-items-center justify-content-center position-relative mb-3"
                  style={{ height: '280px' }}
                >
                  <img
                    src={product.image || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDQwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTAwQzIxMy4yNTUgMTAwIDIyNCAxMTAuNzQ1IDIyNCAxMjRDMjI0IDEzNy4yNTUgMjEzLjI1NSAxNDggMjAwIDE0OEMxODYuNzQ1IDE0OCAxNzYgMTM3LjI1NSAxNzYgMTI0QzE3NiAxMTAuNzQ1IDE4Ni43NDUgMTAwIDIwMCAxMDBaIiBmaWxsPSIjOUNBM0FGIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNkI3MjgwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiPlNlbSBJbWFnZW08L3RleHQ+Cjwvc3ZnPgo="}
                    alt={product.name}
                    className="w-100 h-100"
                    style={{ objectFit: 'cover', filter: 'brightness(0.95)' }}
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDQwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTAwQzIxMy4yNTUgMTAwIDIyNCAxMTAuNzQ1IDIyNCAxMjRDMjI0IDEzNy4yNTUgMjEzLjI1NSAxNDggMjAwIDE0OEMxODYuNzQ1IDE0OCAxNzYgMTM3LjI1NSAxNzYgMTI0QzE3NiAxMTAuNzQ1IDE4Ni43NDUgMTAwIDIwMCAxMDBaIiBmaWxsPSIjOUNBM0FGIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNkI3MjgwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiPlNlbSBJbWFnZW08L3RleHQ+Cjwvc3ZnPgo=";
                    }}
                  />
                  {/* Badge de Afiliado */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <Icon icon="mdi:shield-star" width="18" />
                    Afiliado
                  </div>
                </div>

                {/* Nome do Produto */}
                <span className="d-block mb-3 text-primary-light fw-bold" style={{ fontSize: '24px', lineHeight: '1.3' }}>
                  {product.name}
                </span>

                {/* Código e Status */}
                <div className="mb-3" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(product.product_code || product.code) && (
                    <div style={{
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon icon="mdi:barcode" style={{ fontSize: '18px', color: '#6366f1' }} />
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>
                          Código do Produto
                        </span>
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', fontFamily: 'monospace' }}>
                        {product.product_code || product.code || product.id}
                      </span>
                    </div>
                  )}

                  {/* Categoria */}
                  <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon icon="mdi:shape" style={{ fontSize: '18px', color: '#6366f1' }} />
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>
                        Categoria
                      </span>
                    </div>
                    <span className="badge bg-primary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                      {product.category || 'Sem categoria'}
                    </span>
                  </div>
                </div>

                {/* Descrição */}
                {product.description && (
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                    <div className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon icon="mdi:text-box-outline" style={{ fontSize: '20px', color: '#6366f1' }} />
                      <span className="fw-bold text-neutral-900" style={{ fontSize: '16px' }}>Descrição</span>
                    </div>
                    <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                      {product.description}
                    </p>
                  </div>
                )}

                {/* Sua Comissão */}
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <div
                    className="rounded p-4"
                    style={{
                      background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                      color: 'white',
                      textAlign: 'center'
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '8px', opacity: 0.9 }}>
                      <Icon icon="mdi:percent" className="me-2" />
                      Sua Comissão
                    </span>
                    <span style={{ fontSize: '42px', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                      {affiliateStatus.commissionRate || product.commission_percentage || 0}%
                    </span>
                    <small style={{ fontSize: '13px', opacity: 0.9 }}>
                      Por venda realizada
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Coluna Direita - Ferramentas do Afiliado */}
          <div className="col-lg-7">
            {isApproved && (
              <>
                {loadingAffiliateDetails ? (
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                    <div className="card-body text-center py-5">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <span className="text-muted">Carregando ferramentas...</span>
                    </div>
                  </div>
                ) : affiliateDetails ? (
                  <>
                    {/* Checkouts Disponíveis */}
                    {affiliateDetails.checkouts?.length > 0 && (
                      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
                        <div className="card-body p-4">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <span style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                              <Icon icon="mdi:cart" className="me-2 text-primary" />
                              Checkouts Disponíveis
                            </span>
                            {!firstLink && (
                              <Alert variant="warning" className="mb-0 py-2 px-3" style={{ fontSize: '12px' }}>
                                <Icon icon="mdi:alert" className="me-1" />
                                Crie um link de afiliado para rastrear suas vendas
                              </Alert>
                            )}
                          </div>
                          <div className="checkouts-grid">
                            {affiliateDetails.checkouts.map((checkout) => (
                                <div key={checkout.id} className="checkout-card-grid">
                                  <div className="checkout-card-grid__header">
                                    <div className="checkout-card-grid__avatar">
                                      <Icon icon="mdi:cart" style={{ fontSize: '20px', color: 'white' }} />
                                    </div>
                                    <div className="checkout-card-grid__name">
                                      {checkout.name}
                                    </div>
                                  </div>

                                  <div className="checkout-card-grid__body">
                                    {checkout.plan_name && (
                                      <div className="checkout-card-grid__plan">
                                        <Icon icon="mdi:tag" className="me-1 text-info" style={{ fontSize: '14px' }} />
                                        <span>{checkout.plan_name}</span>
                                        {checkout.plan_price && (
                                          <span className="text-success fw-bold ms-1">
                                            {CurrencyFormatter.centsToFormatted(checkout.plan_price)}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <div className="checkout-card-grid__actions">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => {
                                        // Usar checkoutUrlBuilder para gerar URL correta
                                        const affiliateCode = affiliateDetails?.affiliation?.affiliate_code;
                                        const checkoutUrl = checkoutUrlBuilder.buildUrl(
                                          product,
                                          checkout,
                                          affiliateCode || firstLink?.link_code
                                        );
                                        if (checkoutUrl) {
                                          window.open(checkoutUrl, '_blank');
                                        }
                                      }}
                                      className="flex-fill"
                                    >
                                      <Icon icon="mdi:open-in-new" className="me-1" />
                                      Abrir
                                    </Button>
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => {
                                        // Usar checkoutUrlBuilder para gerar URL correta
                                        const affiliateCode = affiliateDetails?.affiliation?.affiliate_code;
                                        const checkoutUrl = checkoutUrlBuilder.buildUrl(
                                          product,
                                          checkout,
                                          affiliateCode || firstLink?.link_code
                                        );
                                        if (checkoutUrl) {
                                          navigator.clipboard.writeText(checkoutUrl);
                                          setCopySuccess(true);
                                          setTimeout(() => setCopySuccess(false), 3000);
                                        }
                                      }}
                                      className="flex-fill"
                                    >
                                      <Icon icon="mdi:content-copy" className="me-1" />
                                      Copiar
                                    </Button>
                                  </div>
                                </div>
                              ))}

                          </div>
                        </div>
                      </div>
                    )}

                    {/* Seção de Links e URLs */}
                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          {/* Links de Afiliado */}
                          <div className="col-md-6">
                            <div className="h-100 border rounded p-3" style={{ borderRadius: '12px', backgroundColor: '#f8f9fa' }}>
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                                  <Icon icon="mdi:link-variant" className="me-2 text-success" />
                                  Seus Links
                                </span>
                                <button
                                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                                  onClick={() => setShowLinkModal(true)}
                                  style={{ fontSize: '12px', padding: '6px 12px' }}
                                >
                                  <Icon icon="mdi:plus" style={{ fontSize: '16px' }} />
                                  Criar
                                </button>
                              </div>
                              {affiliateDetails.affiliate_links?.length > 0 ? (
                                <div>
                                  {affiliateDetails.affiliate_links.map((link) => {
                                    let campaignName = link.campaign_name || link.link_code.split(/\d/)[0] || link.link_code.substring(0, 10);
                                    if (!campaignName || campaignName.length < 3) {
                                      campaignName = `Link ${link.id}`;
                                    }

                                    return (
                                      <div key={link.id} className="affiliate-link-card">
                                        <div className="affiliate-link-card__icon">
                                          <Icon icon="mdi:link-variant" style={{ fontSize: '20px', color: '#28a745' }} />
                                        </div>
                                        <div className="affiliate-link-card__info">
                                          <div className="affiliate-link-card__name">
                                            {campaignName}
                                          </div>
                                          <div className="affiliate-link-card__code">
                                            <code>{link.link_code}</code>
                                          </div>
                                          <div className="affiliate-link-card__stats">
                                            <Icon icon="mdi:mouse" className="me-1" style={{ fontSize: '14px' }} />
                                            {link.clicks_count || 0} cliques
                                          </div>
                                        </div>
                                        <div className="affiliate-link-card__actions">
                                          <Button
                                            variant="outline-success"
                                            size="sm"
                                            onClick={() => {
                                              // Regenerar URL usando checkoutUrlBuilder (não depender de full_url do banco)
                                              const firstCheckout = affiliateDetails?.checkouts?.[0];
                                              const affiliateCode = affiliateDetails?.affiliation?.affiliate_code;

                                              if (firstCheckout && affiliateCode) {
                                                const linkToCopy = checkoutUrlBuilder.buildUrl(
                                                  product,
                                                  firstCheckout,
                                                  affiliateCode
                                                );
                                                if (linkToCopy) {
                                                  navigator.clipboard.writeText(linkToCopy);
                                                  setCopySuccess(true);
                                                  setTimeout(() => setCopySuccess(false), 3000);
                                                }
                                              } else {
                                                alert('Checkout ou affiliate_code não disponível.');
                                              }
                                            }}
                                          >
                                            <Icon icon="mdi:content-copy" className="me-1" />
                                            Copiar
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center text-muted py-3">
                                  <Icon icon="mdi:link-variant-off" style={{ fontSize: '32px' }} className="mb-2 d-block mx-auto" />
                                  <p className="mb-0 small">Nenhum link criado</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* URLs do Produto */}
                          <div className="col-md-6">
                            <div className="h-100 border rounded p-3" style={{ borderRadius: '12px', backgroundColor: '#f8f9fa' }}>
                              <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                <Icon icon="mdi:web" className="me-2 text-info" />
                                URLs do Produto
                              </span>
                              {affiliateDetails.product_urls?.length > 0 ? (
                                <div>
                                  {affiliateDetails.product_urls.map((url) => (
                                    <div key={url.id} className="product-url-card">
                                      <div className="product-url-card__icon">
                                        <Icon icon="mdi:web" style={{ fontSize: '20px', color: '#17a2b8' }} />
                                      </div>
                                      <div className="product-url-card__info">
                                        <div className="product-url-card__name">
                                          {url.description}
                                        </div>
                                        <div className="product-url-card__url">
                                          {url.url}
                                        </div>
                                        <div className="product-url-card__stats">
                                          <Icon icon="mdi:cart" className="me-1" style={{ fontSize: '14px' }} />
                                          {url.sales_count || 0} vendas
                                        </div>
                                      </div>
                                      <div className="product-url-card__actions">
                                        <Button
                                          variant="outline-info"
                                          size="sm"
                                          onClick={() => window.open(url.url, '_blank')}
                                        >
                                          <Icon icon="mdi:open-in-new" className="me-1" />
                                          Abrir
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center text-muted py-3">
                                  <Icon icon="mdi:web-off" style={{ fontSize: '32px' }} className="mb-2 d-block mx-auto" />
                                  <p className="mb-0 small">Nenhuma URL cadastrada</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Materiais de Marketing */}
                    {affiliateDetails.marketing_materials?.length > 0 && (
                      <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                        <div className="card-body p-4">
                          <span style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                            <Icon icon="mdi:palette" className="me-2 text-warning" />
                            Materiais de Marketing
                          </span>
                          <div className="row g-3">
                            {affiliateDetails.marketing_materials.map((material) => (
                              <div key={material.id} className="col-md-6">
                                <div className="border rounded p-3 h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <span style={{ fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                                        {material.name}
                                      </span>
                                      <small className="text-muted" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>
                                        {material.file_type}
                                      </small>
                                      <small className="text-warning" style={{ fontSize: '12px' }}>
                                        {material.downloads_count || 0} downloads
                                      </small>
                                    </div>
                                    <button
                                      className="btn btn-outline-warning btn-sm d-flex align-items-center gap-1"
                                      onClick={() => window.open(material.file_url, '_blank')}
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                    >
                                      <Icon icon="mdi:download" style={{ fontSize: '14px' }} />
                                      Baixar
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                    <div className="card-body text-center text-muted py-4">
                      <Icon icon="mdi:information" className="me-2" />
                      <span style={{ fontSize: '14px' }}>Carregue os recursos específicos do afiliado</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal para Criar Link de Afiliado */}
      <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <Icon icon="mdi:link-plus" className="me-2 text-success" />
            Criar Link de Afiliado
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center">
                <Icon icon="mdi:tag" className="me-1" />
                Nome da Campanha *
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: WhatsApp, Instagram, Email..."
                value={linkForm.campaign_name}
                onChange={(e) => setLinkForm(prev => ({ ...prev, campaign_name: e.target.value }))}
                maxLength={50}
              />
              <Form.Text className="text-muted">
                Este nome ajudará você a identificar o link nas estatísticas
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center">
                <Icon icon="mdi:clock" className="me-1" />
                Duração do Cookie
              </Form.Label>
              <Form.Select
                value={linkForm.cookie_duration_hours}
                onChange={(e) => setLinkForm(prev => ({ ...prev, cookie_duration_hours: parseInt(e.target.value) }))}
              >
                <option value={24}>1 dia</option>
                <option value={168}>7 dias (padrão)</option>
                <option value={360}>15 dias</option>
                <option value={720}>30 dias</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Tempo que o link manterá o tracking após o primeiro clique
              </Form.Text>
            </Form.Group>

            {linkForm.campaign_name && (
              <div className="alert alert-info">
                <strong>Preview do link:</strong>
                <br />
                <code className="small">
                  {`${FRONTEND_URL}/#/checkout/${product?.product_code || id}/${affiliateDetails?.checkouts?.[0]?.checkout_code || 'chk_xxx'}?aff=${affiliateDetails?.affiliation?.affiliate_code || 'aff_xxx'}`}
                </code>
                <br />
                <small className="text-muted mt-1 d-block">
                  Campanha: {linkForm.campaign_name}
                </small>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLinkModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="success" 
            onClick={createAffiliateLink}
            disabled={!linkForm.campaign_name.trim() || creatingLink}
          >
            {creatingLink ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Criando...
              </>
            ) : (
              <>
                <Icon icon="mdi:plus" className="me-1" />
                Criar Link
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </MasterLayout>
  );
};

export default AffiliateProductDetailsPage;
